import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Footer from '../../components/Footer';
import TechLogo from '../../components/TechLogo';
import ContentBlocks from '../../components/ContentBlocks';
import { technologies, learnGroups, featuredGroup, groupStyles, getTechnology } from '../../data/learn';
import './index.css';

// Docs-style Learn section: one sidebar with every technology and lesson,
// content on the right. Handles /learn, /learn/:tech and /learn/:tech/:lesson.
const Learn = () => {
  const { tech: techSlug, lesson: lessonSlug } = useParams();
  const tech = techSlug ? getTechnology(techSlug) : null;
  const lessonIndex = tech
    ? (lessonSlug ? tech.lessons.findIndex((l) => l.slug === lessonSlug) : 0)
    : -1;
  const lesson = tech?.lessons[lessonIndex];

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set(techSlug ? [techSlug] : []));
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Open the current technology in the sidebar and close the mobile drawer on navigation
  useEffect(() => {
    if (techSlug) setExpanded((prev) => (prev.has(techSlug) ? prev : new Set(prev).add(techSlug)));
    setDrawerOpen(false);
  }, [techSlug, lessonSlug]);

  useEffect(() => {
    document.title = tech && lesson ? `${lesson.title} · ${tech.name} | Codeazy` : 'Learn | Codeazy';
    return () => { document.title = 'Codeazy'; };
  }, [tech, lesson]);

  const filteredTechs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return technologies.map((t) => ({ tech: t, lessons: t.lessons }));
    return technologies
      .map((t) => {
        const nameMatches = t.name.toLowerCase().includes(q);
        const lessons = nameMatches ? t.lessons : t.lessons.filter((l) => l.title.toLowerCase().includes(q));
        return { tech: t, lessons };
      })
      .filter(({ lessons }) => lessons.length > 0);
  }, [query]);

  const toggle = (slug) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  if (techSlug && !tech) return <Navigate to="/learn" replace />;
  if (tech && !lesson) return <Navigate to={`/learn/${tech.slug}`} replace />;

  const searching = query.trim() !== '';

  const sidebar = (
    <nav className="docs-nav" aria-label="Learn topics">
      <label className="docs-search">
        <SearchIcon sx={{ fontSize: 18 }} />
        <input
          type="search"
          placeholder="Search lessons"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search lessons"
        />
      </label>

      {filteredTechs.length === 0 && <p className="docs-nav-empty">No lessons found.</p>}

      {learnGroups.map((group) => {
        const items = filteredTechs.filter(({ tech: t }) => t.group === group);
        if (items.length === 0) return null;
        const featured = group === featuredGroup.name;
        const { label, icon: GroupIcon, accent } = groupStyles[group];
        return (
          <div
            className={`docs-nav-group docs-nav-card ${featured ? 'is-featured' : ''}`}
            key={group}
            style={{ '--group-1': accent[0], '--group-2': accent[1] }}
          >
            <h3 className="docs-card-title"><GroupIcon sx={{ fontSize: 14 }} /> {label}</h3>
            <ul>
              {items.map(({ tech: t, lessons }) => {
                const isOpen = searching || expanded.has(t.slug);
                const isCurrentTech = tech?.slug === t.slug;
                return (
                  <li key={t.slug}>
                    <button
                      type="button"
                      className={`docs-nav-tech ${isCurrentTech ? 'current' : ''}`}
                      onClick={() => toggle(t.slug)}
                      aria-expanded={isOpen}
                    >
                      <ChevronRightIcon className={`docs-chevron ${isOpen ? 'open' : ''}`} sx={{ fontSize: 18 }} />
                      <TechLogo tech={t} size={22} />
                      <span>{t.name}</span>
                      <em className="docs-nav-count">{t.lessons.length}</em>
                    </button>
                    {isOpen && (
                      <ul className="docs-nav-lessons">
                        {lessons.map((l) => {
                          const active = isCurrentTech && lesson?.slug === l.slug;
                          return (
                            <li key={l.slug}>
                              <Link
                                to={`/learn/${t.slug}/${l.slug}`}
                                className={active ? 'active' : ''}
                                aria-current={active ? 'page' : undefined}
                              >
                                {l.title}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="page docs-page">
      <div className="docs-mobile-bar">
        <button
          type="button"
          className="docs-mobile-toggle"
          onClick={() => setDrawerOpen(true)}
          aria-expanded={drawerOpen}
          aria-label={tech ? `Change topic (current: ${tech.name}, ${lesson.title})` : 'Choose a topic'}
        >
          <span className="docs-mobile-toggle-icon">
            <MenuBookIcon sx={{ fontSize: 18 }} />
          </span>
          <span className="docs-mobile-toggle-text">
            <small>{tech ? tech.name : 'Topics'}</small>
            <strong>{tech ? lesson.title : 'Choose what to learn'}</strong>
          </span>
          <span className="docs-mobile-toggle-cta">
            {tech ? 'Select' : 'Choose'}
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          </span>
        </button>
      </div>

      <div className="docs-layout">
        <aside className={`docs-sidebar ${drawerOpen ? 'open' : ''}`}>
          <div className="docs-drawer-header">
            <span>Topics</span>
            <button type="button" onClick={() => setDrawerOpen(false)} aria-label="Close topics">
              <CloseIcon sx={{ fontSize: 20 }} />
            </button>
          </div>
          {sidebar}
        </aside>
        {drawerOpen && <div className="docs-backdrop" onClick={() => setDrawerOpen(false)} />}

        <main className="docs-main">
          {tech ? <LessonView tech={tech} lesson={lesson} lessonIndex={lessonIndex} /> : <Overview />}
        </main>
      </div>

      <Footer />
    </div>
  );
};

const Overview = () => (
  <article className="docs-content">
    <header className="docs-header">
      <span className="docs-eyebrow">Learn</span>
      <h1>Programming &amp; software concepts</h1>
      <p>Free, bite-sized lessons with examples. Pick a topic from the menu, or start with one of the technologies below.</p>
    </header>

    {learnGroups.map((group) => {
      const { label, icon: GroupIcon, accent } = groupStyles[group];
      return (
      <section
        className={`docs-overview-group docs-overview-card ${group === featuredGroup.name ? 'is-featured' : ''}`}
        key={group}
        style={{ '--group-1': accent[0], '--group-2': accent[1] }}
      >
        <h2 className="docs-card-heading"><GroupIcon sx={{ fontSize: 20 }} /> {label}</h2>
        <div className="docs-overview-list">
          {technologies.filter((t) => t.group === group).map((t) => (
            <Link to={`/learn/${t.slug}`} className="docs-overview-item" key={t.slug}>
              <TechLogo tech={t} size={36} />
              <div>
                <strong>{t.name}</strong>
                <span>{t.description}</span>
              </div>
              <em>{t.lessons.length} lessons</em>
            </Link>
          ))}
        </div>
      </section>
      );
    })}
  </article>
);

const LessonView = ({ tech, lesson, lessonIndex }) => {
  const prev = tech.lessons[lessonIndex - 1];
  const next = tech.lessons[lessonIndex + 1];

  return (
    <article className="docs-content" style={{ '--accent-1': tech.accent[0] }}>
      <header className="docs-header">
        <span className="docs-eyebrow">
          <Link to="/learn">Learn</Link> / {tech.name} · Lesson {lessonIndex + 1} of {tech.lessons.length}
        </span>
        <h1>{lesson.title}</h1>
        <p>{lesson.summary}</p>
        <span className="docs-read-time">{lesson.readTime} min read</span>
      </header>

      <ContentBlocks blocks={lesson.content} />

      <nav className="docs-pager" aria-label="Lesson navigation">
        {prev ? (
          <Link to={`/learn/${tech.slug}/${prev.slug}`} className="docs-pager-link">
            <span><ArrowBackIcon sx={{ fontSize: 16 }} /> Previous</span>
            <strong>{prev.title}</strong>
          </Link>
        ) : <span />}
        {next ? (
          <Link to={`/learn/${tech.slug}/${next.slug}`} className="docs-pager-link next">
            <span>Next <ArrowForwardIcon sx={{ fontSize: 16 }} /></span>
            <strong>{next.title}</strong>
          </Link>
        ) : (
          <Link to="/learn" className="docs-pager-link next">
            <span>Finished <ArrowForwardIcon sx={{ fontSize: 16 }} /></span>
            <strong>Explore another technology</strong>
          </Link>
        )}
      </nav>
    </article>
  );
};

export default Learn;
